import type { ReactNode } from 'react'
import type { UseQueryResult } from '@tanstack/react-query'
import type { ApiError } from '../api/problem'
import { ProblemBanner } from './ProblemBanner'

interface Props<T> {
  query: UseQueryResult<T, ApiError>
  /** A skeleton of the real layout, not a spinner on an empty page. */
  skeleton?: ReactNode
  /** Shown when the request succeeded and there was nothing in it. */
  empty?: ReactNode
  /**
   * Shown instead of an error banner when the API answers 404.
   *
   * On a page that exists, a 404 is usually a fact rather than a failure:
   * there was no constructors' championship in 1950, and saying "something
   * went wrong" about that would be wrong.
   */
  absent?: ReactNode
  children: (data: T) => ReactNode
}

/**
 * The four states every page body can be in, in one place and used everywhere.
 *
 * The fourth is the one that is usually forgotten: `isPlaceholderData` means
 * the previous answer is still on screen while a new scenario loads. Dimming
 * it rather than blanking it is what makes adjusting a control feel like the
 * view changing instead of the page reloading.
 */
export function DataState<T>({ query, skeleton, empty, absent, children }: Props<T>) {
  if (query.isPending) {
    return <div className="state state--loading">{skeleton ?? <Skeleton />}</div>
  }
  if (query.error) {
    if (absent !== undefined && query.error.problem.status === 404) {
      return <p className="state state--empty">{absent}</p>
    }
    return <ProblemBanner problem={query.error.problem} onRetry={() => void query.refetch()} />
  }
  if (query.data === undefined || isEmpty(query.data)) {
    return <p className="state state--empty">{empty ?? 'Nothing here.'}</p>
  }
  return (
    <div className={query.isPlaceholderData ? 'state state--stale' : undefined}>
      {children(query.data)}
    </div>
  )
}

function isEmpty(data: unknown): boolean {
  if (Array.isArray(data)) return data.length === 0
  if (data && typeof data === 'object' && 'items' in data) {
    return Array.isArray(data.items) && data.items.length === 0
  }
  return false
}

export function Skeleton({ rows = 6 }: { rows?: number }) {
  return (
    <div aria-hidden="true" className="skeleton">
      {Array.from({ length: rows }, (_, index) => (
        <span key={index} className="skeleton__row" />
      ))}
    </div>
  )
}
